import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instructorApi, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';

// Exported so tests can seed sessionStorage with the same key the component
// reads, instead of duplicating the literal.
export const TOKEN_KEY = 'obs.instructor.token';

interface Toggle {
  key: string;
  label: string;
  type: 'bool' | 'int' | 'float' | 'enum';
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
}

const TOGGLES: Toggle[] = [
  { key: 'catalog.search_slow_ms', label: 'Catalog search slow (ms)', type: 'int', min: 0, max: 5000, step: 50 },
  // API stores this as a float fraction (0..1), not a percent — an 'int'
  // control here used to send whole numbers like 5, which services read as
  // a 500% failure rate.
  { key: 'catalog.flaky_rate', label: 'Catalog flaky rate (0-1)', type: 'float', min: 0, max: 1, step: 0.05 },
  { key: 'cart.leak_kb_per_op', label: 'Cart leak KB / op', type: 'int', min: 0, max: 1024, step: 16 },
  { key: 'checkout.cpu_burn_ms', label: 'Checkout CPU burn (ms)', type: 'int', min: 0, max: 5000, step: 100 },
  { key: 'checkout.cascade_depth', label: 'Checkout cascade depth', type: 'int', min: 0, max: 8, step: 1 },
  { key: 'payment.mode', label: 'Payment mode', type: 'enum', options: ['success', 'slow', 'timeout', 'reject', 'down'] },
  // Seconds of sleep the worker waits before each read; 0 means off. The
  // runbook's demo preset uses 150, well past the old max of 20.
  //
  // Catalog and the worker agree on 0.0 for an unset key
  // (apps/catalog/src/catalog/models.py CHAOS_TOGGLE_DEFAULTS,
  // apps/worker/src/worker/consumer.py), so the number the API returns is
  // the number the worker honours and the row renders it verbatim. The max
  // matches catalog's CHAOS_TOGGLE_RANGES ceiling — anything higher 422s.
  {
    key: 'worker.backlog_factor',
    label: 'Worker backlog factor (seconds of sleep before each read, 0 = off)',
    type: 'int',
    min: 0,
    max: 300,
    step: 10,
  },
  // Same float-not-percent issue as catalog.flaky_rate.
  { key: 'worker.fail_rate', label: 'Worker fail rate (0-1)', type: 'float', min: 0, max: 1, step: 0.05 },
  { key: 'db.slow_query_ms', label: 'DB slow query (ms)', type: 'int', min: 0, max: 5000, step: 100 },
  { key: 'db.bypass_index', label: 'DB bypass GIN index', type: 'bool' },
];

export function Instructor() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [tokenInput, setTokenInput] = useState('');
  const [showDialog, setShowDialog] = useState(!token);
  const qc = useQueryClient();
  // Bumped on every successful Reset all. A numeric row's server value can
  // come back unchanged after a reset (e.g. it was already 0), so the draft
  // resync can't rely on the server value alone having changed — it needs
  // this as an extra signal.
  const [resetNonce, setResetNonce] = useState(0);

  const stateQ = useQuery({
    queryKey: ['instructor', 'state', token],
    queryFn: async () => (await instructorApi.state(token!)).data,
    enabled: !!token,
    retry: false,
  });

  // Re-prompt on 401.
  useEffect(() => {
    const err = stateQ.error as ApiError | null;
    if (err?.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setShowDialog(true);
      toast.error('Instructor token rejected');
    }
  }, [stateQ.error]);

  const toggleMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      instructorApi.toggle(token!, key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instructor', 'state'] }),
    // `description` gets the detail text (e.g. the range a 422 named) —
    // `toast.error(err.message)` alone rendered it as the toast's title,
    // one line with truncation, easy to lose next to a long key name.
    onError: (err: Error & { traceId?: string | null }) =>
      toast.error('Toggle rejected', { description: err.message }),
  });

  const resetMut = useMutation({
    mutationFn: () => instructorApi.reset(token!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instructor', 'state'] });
      setResetNonce((n) => n + 1);
      toast.success('Chaos reset.');
    },
  });

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="font-display text-3xl">Instructor Panel</h1>
        <p className="text-(--color-ink-muted) text-sm">
          Token is held in <code>sessionStorage</code> and cleared on tab close.
          Never persisted to runtime config or the bundle.
        </p>
      </header>

      <Dialog
        open={showDialog}
        onOpenChange={(o) => {
          if (!o && !token) return; // can't dismiss without token
          setShowDialog(o);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Instructor token required</DialogTitle>
            <DialogDescription>
              Enter the shared instructor secret to access chaos toggles.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!tokenInput.trim()) return;
              sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
              setToken(tokenInput.trim());
              setTokenInput('');
              setShowDialog(false);
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="token">Token</Label>
              <Input
                id="token"
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="submit" variant="cta">Unlock</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {!token && <p className="text-(--color-ink-muted)">Awaiting token…</p>}

      {token && stateQ.isLoading && <Skeleton className="h-64" />}

      {token && stateQ.data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-(--color-ink-muted)">Current chaos state</p>
            <Button variant="outline" size="sm" onClick={() => resetMut.mutate()}>
              Reset all
            </Button>
          </div>
          <Separator />
          <ul className="space-y-3">
            {TOGGLES.map((t) => {
              const v = (stateQ.data as Record<string, unknown>)[t.key];
              return (
                <li key={t.key} className="rounded-md border bg-card p-3 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{t.label}</p>
                    <p className="text-xs text-(--color-ink-muted)">{t.key} · current: {String(v ?? '—')}</p>
                  </div>
                  <ToggleControl
                    toggle={t}
                    value={v}
                    onChange={(value) => toggleMut.mutate({ key: t.key, value })}
                    disabled={toggleMut.isPending}
                    resetNonce={resetNonce}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function ToggleControl({
  toggle,
  value,
  onChange,
  disabled,
  resetNonce,
}: {
  toggle: Toggle;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  resetNonce: number;
}) {
  if (toggle.type === 'bool') {
    return (
      <Switch
        checked={!!value}
        onCheckedChange={(checked) => onChange(checked)}
        disabled={disabled}
        aria-label={toggle.label}
      />
    );
  }
  if (toggle.type === 'enum') {
    return (
      <select
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={toggle.label}
        className="h-9 rounded-md border bg-background px-2 text-sm"
      >
        {(toggle.options ?? []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  // 'int' and 'float': typing must not write on every keystroke — the
  // runbook's instruction is "set X, click apply". Local draft state decouples
  // editing from the mutation; Apply (or Enter) is the only thing that fires it.
  return (
    <NumericControl
      toggle={toggle}
      value={value}
      onApply={onChange}
      disabled={disabled}
      resetNonce={resetNonce}
    />
  );
}

function parseNumeric(toggle: Toggle, raw: string): number {
  return toggle.type === 'float' ? parseFloat(raw) : parseInt(raw, 10);
}

function NumericControl({
  toggle,
  value,
  onApply,
  disabled,
  resetNonce,
}: {
  toggle: Toggle;
  value: unknown;
  onApply: (v: number) => void;
  disabled?: boolean;
  resetNonce: number;
}) {
  const serverValue = typeof value === 'number' ? value : 0;
  const [draft, setDraft] = useState(String(serverValue));

  // Re-sync the draft to the server value after a successful apply, or on a
  // Reset all even when the server value comes back unchanged (e.g. it was
  // already 0) — resetNonce covers that case. A background refetch that
  // returns the same value and doesn't come from a reset must NOT clobber
  // whatever the instructor is mid-typing.
  useEffect(() => {
    setDraft(String(serverValue));
  }, [serverValue, resetNonce]);

  const parsed = parseNumeric(toggle, draft);
  const inRange =
    Number.isFinite(parsed) &&
    (toggle.min === undefined || parsed >= toggle.min) &&
    (toggle.max === undefined || parsed <= toggle.max);
  const unchanged = Number.isFinite(parsed) && parsed === serverValue;
  const applyDisabled = !!disabled || !inRange || unchanged;

  const apply = () => {
    if (applyDisabled) return;
    onApply(parsed);
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        className="w-28 h-9"
        min={toggle.min}
        max={toggle.max}
        step={toggle.step}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
        }}
        disabled={disabled}
        aria-label={toggle.label}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={apply}
        disabled={applyDisabled}
        aria-label={`Apply ${toggle.label}`}
      >
        Apply
      </Button>
    </div>
  );
}
